import * as THREE from "three";

export const PLANET_RADIUS = 16;
export const PLAYER_SPEED = 4.1;
export const NPC_SPEED = 3.2;
export const INTERACT_RADIUS = 2.7;
export const LANDMARK_RADIUS = 3.2;

export function latLonToVector3(lat: number, lon: number, radius: number): THREE.Vector3 {
  const phi = THREE.MathUtils.degToRad(90 - lat);
  const theta = THREE.MathUtils.degToRad(lon);
  return new THREE.Vector3().setFromSphericalCoords(radius, phi, theta);
}

export function stickToSphere(pos: THREE.Vector3, radius: number, target = new THREE.Vector3()): THREE.Vector3 {
  return target.copy(pos).normalize().multiplyScalar(radius);
}

export function tangentBasis(up: THREE.Vector3): { east: THREE.Vector3; north: THREE.Vector3; up: THREE.Vector3 } {
  const radial = up.clone().normalize();
  const east = new THREE.Vector3(0, 1, 0).cross(radial);
  if (east.lengthSq() < 1e-6) {
    east.set(1, 0, 0).cross(radial);
  }
  east.normalize();
  const north = radial.clone().cross(east).normalize();
  return { east, north, up: radial };
}

export function moveOnSphere(
  pos: THREE.Vector3,
  moveWorld: THREE.Vector3,
  distance: number,
  radius: number,
): THREE.Vector3 {
  if (distance < 1e-8 || moveWorld.lengthSq() < 1e-8) {
    return stickToSphere(pos, radius);
  }
  const up = pos.clone().normalize();
  const dir = moveWorld.clone().projectOnPlane(up);
  if (dir.lengthSq() < 1e-8) {
    return stickToSphere(pos, radius);
  }
  dir.normalize();
  const axis = new THREE.Vector3().crossVectors(up, dir).normalize();
  return pos.clone().applyAxisAngle(axis, distance / radius).normalize().multiplyScalar(radius);
}

export function greatCircleStep(
  from: THREE.Vector3,
  to: THREE.Vector3,
  distance: number,
  radius: number,
): THREE.Vector3 {
  const a = from.clone().normalize();
  const b = to.clone().normalize();
  const axis = new THREE.Vector3().crossVectors(a, b);
  if (axis.lengthSq() < 1e-8) {
    return stickToSphere(from, radius);
  }
  axis.normalize();
  const angle = Math.min(distance / radius, a.angleTo(b));
  return from.clone().applyAxisAngle(axis, angle).normalize().multiplyScalar(radius);
}

export function surfaceDistance(a: THREE.Vector3, b: THREE.Vector3, radius: number): number {
  return a.clone().normalize().angleTo(b.clone().normalize()) * radius;
}

const _up = new THREE.Vector3();
const _x = new THREE.Vector3();
const _z = new THREE.Vector3();
const _m = new THREE.Matrix4();
const _y0 = new THREE.Vector3(0, 1, 0);

export function orientOnSphere(obj: THREE.Object3D, surfacePos: THREE.Vector3, faceDir?: THREE.Vector3): void {
  _up.copy(surfacePos).normalize();
  obj.position.copy(surfacePos);
  obj.up.copy(_up);
  if (faceDir && faceDir.lengthSq() > 1e-8) {
    _z.copy(faceDir).projectOnPlane(_up);
    if (_z.lengthSq() < 1e-8) {
      obj.quaternion.setFromUnitVectors(_y0, _up);
      return;
    }
    _z.normalize().negate();
    _x.crossVectors(_up, _z);
    if (_x.lengthSq() < 1e-8) {
      obj.quaternion.setFromUnitVectors(_y0, _up);
      return;
    }
    _x.normalize();
    _z.crossVectors(_x, _up).normalize();
    _m.makeBasis(_x, _up, _z);
    obj.quaternion.setFromRotationMatrix(_m);
    return;
  }
  obj.quaternion.setFromUnitVectors(_y0, _up);
}

export function facingCamera(worldPos: THREE.Vector3, cameraPos: THREE.Vector3): number {
  const normal = worldPos.clone().normalize();
  const toCam = cameraPos.clone().sub(worldPos).normalize();
  return normal.dot(toCam);
}

export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a += 0x6d2b79f5;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
